import React, { useContext, useState, useEffect } from 'react';
import { ThemeContext } from '../theme';
import ProfileTabBar from '../components/ProfileTabBar';

const SCALE = 0.75;
const sc = (n) => Math.round(n * SCALE);

// Outer donut: dark teal (video+audio = 89.3%) + light teal (text = 10.7%)
// Inner solid pie: video (tan 57%), audio (brown 32%), text (dark 11%)
// Both rings start at CHART_START so the light outer arc and dark text slice align at top
const CHART_START = 0; // both rings start at 12 o'clock

// Outer ring: light from 12→2 o'clock (1/6), dark for the rest (5/6)
const OUTER_RING = [
  { value: 1, color: '#95acb6' }, // Charcoal-Green 400 — 12 to 2 o'clock
  { value: 5, color: '#5d7b88' }, // Charcoal-Green 600 — 2 to 12 o'clock
];
// mid = 86/172 = exactly 50%, dark = 22, light = 64
const INNER_PIE = [
  { value: 64, color: '#b7967a' }, // Peach-Brown 600 — light brown
  { value: 86, color: '#7a6451' }, // Peach-Brown 700 — mid brown (50%)
  { value: 22, color: '#3d3229' }, // Peach-Brown 800 — dark brown
];

function polarToXY(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function DonutChart({ centerColor, separatorColor }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const cx = 130, cy = 130;
  const outerR = 82, outerSW = 32;
  const innerR = 44;
  const C = 2 * Math.PI * outerR;

  // Outer ring — dark is always a full circle; light arc draws in clockwise from 12→2
  const lightDash = (OUTER_RING[0].value / (OUTER_RING[0].value + OUTER_RING[1].value)) * C;
  const outerArcs = (
    <>
      {/* Dark full circle — always visible */}
      <circle cx={cx} cy={cy} r={outerR} fill="none"
        stroke={OUTER_RING[1].color} strokeWidth={outerSW}
      />
      {/* Light arc — draws in clockwise from 12 o'clock */}
      <circle cx={cx} cy={cy} r={outerR} fill="none"
        stroke={OUTER_RING[0].color} strokeWidth={outerSW}
        strokeDasharray={`${ready ? lightDash : 0} ${C}`}
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: `${cx}px ${cy}px`,
          transition: 'stroke-dasharray 0.45s ease-out',
        }}
      />
    </>
  );

  // Inner circle — stacked stroke circles (same technique as outer ring)
  // dark = full base; mid draws 12→10 (10/12); light draws 12→4 (4/12)
  const innerSR = innerR / 2;
  const C_inner = 2 * Math.PI * innerSR;
  const innerMidDash   = (10 / 12) * C_inner;
  const innerLightDash = (4  / 12) * C_inner;

  const scaleAnim = ready ? 'chartScaleIn 0.45s ease-out both' : 'none';

  return (
    <svg viewBox="0 0 260 260" width="100%" height="100%" style={{ display: 'block' }}>
      <style>{`
        @keyframes chartScaleIn {
          from { transform: scale(0.8); }
          to   { transform: scale(1); }
        }
      `}</style>

      {/* Static grey background — no animation */}
      <rect x="15" y="15" width="230" height="230" rx="16" ry="16" fill={centerColor} />

      {/* Outer ring — dark always visible, light draws in */}
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: scaleAnim }}>
        {outerArcs}
        <circle cx={cx} cy={cy} r={outerR - outerSW / 2} fill={separatorColor} />
      </g>

      {/* Inner circle */}
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: ready ? 'chartScaleIn 0.45s ease-out 0.15s both' : 'none' }}>
        <circle cx={cx} cy={cy} r={innerSR} fill="none"
          stroke={INNER_PIE[2].color} strokeWidth={innerR}
        />
        <circle cx={cx} cy={cy} r={innerSR} fill="none"
          stroke={INNER_PIE[1].color} strokeWidth={innerR}
          strokeDasharray={`${ready ? innerMidDash : 0} ${C_inner}`}
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: `${cx}px ${cy}px`,
            transition: 'stroke-dasharray 0.5s ease-out 0.15s',
          }}
        />
        <circle cx={cx} cy={cy} r={innerSR} fill="none"
          stroke={INNER_PIE[0].color} strokeWidth={innerR}
          strokeDasharray={`${ready ? innerLightDash : 0} ${C_inner}`}
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: `${cx}px ${cy}px`,
            transition: 'stroke-dasharray 0.4s ease-out 0.4s',
          }}
        />
      </g>
    </svg>
  );
}

const STATS_LEFT = [
  { value: '01 hr 52 mins', label: 'Total Watch Time', dividerColor: '#5d7b88' },
  { value: '43 mins',       label: 'Active Screen Time', dividerColor: '#5d7b88' },
  { value: '01 hr 09 mins', label: 'Passive Screen Time', dividerColor: '#5d7b88' },
];

const STATS_RIGHT = [
  { value: '36 mins',       label: 'Audio Stories (Total Time Spent)',      dividerColor: '#B7967A' },
  { value: '01 hr 04 mins', label: 'Video Stories (Total Time Spent)',      dividerColor: '#B7967A' },
  { value: '12 mins',       label: 'Text-based Stories (Total Time Spent)', dividerColor: '#B7967A' },
];

// left-column bars: [color, widthFraction]
const BARS = [
  ['#5d7b88', 1.00], // Charcoal-Green 600
  ['#b7967a', 0.83], // Peach-Brown 600
  ['#7a6451', 0.49], // Peach-Brown 700
  ['#3d3229', 0.15], // Peach-Brown 800
];

// 4 story count boxes: [color, count, label]
const BOXES = [
  ['#5d7b88', '19', 'Total No. of stories completed'],
  ['#7a6451', '16', 'Total No. of video stories completed'],
  ['#b7967a', '02', 'Total No. of audio stories completed'],
  ['#3d3229', '01', 'Total No. of text stories completed'],
];

function StatRow({ value, label, dividerColor }) {
  const theme = useContext(ThemeContext);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: sc(4) }}>
      <span className="font-satoshi font-bold" style={{ fontSize: sc(24), lineHeight: `${sc(32)}px`, color: theme.text100 }}>
        {value}
      </span>
      <span className="font-satoshi" style={{ fontSize: sc(16), lineHeight: `${sc(24)}px`, color: theme.text400, fontWeight: 500 }}>
        {label}
      </span>
      <div style={{ height: 2, backgroundColor: dividerColor, marginTop: sc(10) }} />
    </div>
  );
}

function StoryBox({ color, count, label }) {
  return (
    <div
      style={{
        backgroundColor: color,
        borderRadius: sc(8),
        padding: `${sc(12)}px ${sc(16)}px`,
        display: 'flex',
        alignItems: 'center',
        gap: sc(14),
        height: sc(70),
        boxSizing: 'border-box',
      }}
    >
      <span className="font-satoshi font-bold" style={{ fontSize: sc(24), color: '#FFFFFF', lineHeight: `${sc(32)}px`, flexShrink: 0 }}>
        {count}
      </span>
      <span className="font-satoshi" style={{ fontSize: sc(14), color: '#FFFFFF', lineHeight: `${sc(20)}px`, fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
}

export default function StatisticsScreen({ onTabChange }) {
  const theme = useContext(ThemeContext);
  const [barsReady, setBarsReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setBarsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: theme.cardBg100, overflow: 'hidden' }}>

      {/* White rounded card */}
      <div
        style={{
          flex: 1,
          margin: sc(16),
          backgroundColor: theme.cardBg,
          borderRadius: sc(30),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ProfileTabBar active="statistics" onTabChange={onTabChange} />

        {/* Main content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: sc(20),
            padding: `${sc(20)}px ${sc(48)}px`,
            margin: 'auto 0',
          }}
        >
          {/* ── Top row: donut + two stat columns ── */}
          <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
            {/* Donut chart */}
            <div style={{ width: '32%', flexShrink: 0, backgroundColor: theme.cardBg100, borderRadius: sc(16), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: sc(8) }}>
              <div style={{ width: '100%', aspectRatio: '1 / 1' }}>
                <DonutChart centerColor={theme.cardBg100} separatorColor={theme.cardBg} />
              </div>
            </div>

            {/* Stats — 2-column grid matching the box grid below */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(3, auto)', gridAutoFlow: 'column', rowGap: sc(24), columnGap: 16 }}>
              {STATS_LEFT.map((item, i) => (
                <StatRow key={i} value={item.value} label={item.label} dividerColor={item.dividerColor} />
              ))}
              {STATS_RIGHT.map((item, i) => (
                <StatRow key={`r${i}`} value={item.value} label={item.label} dividerColor={item.dividerColor} />
              ))}
            </div>
          </div>

          {/* ── Bottom row: bar legend + 2×2 boxes ── */}
          <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
            {/* Bar legend */}
            <div style={{ width: '32%', flexShrink: 0, backgroundColor: theme.cardBg100, borderRadius: sc(16), padding: `${sc(16)}px`, display: 'flex', flexDirection: 'column', gap: sc(10), justifyContent: 'center' }}>
              {BARS.map(([color, fraction], i) => (
                <div key={i} style={{ height: sc(22), backgroundColor: color, borderRadius: sc(6), width: barsReady ? `${fraction * 100}%` : '0%', transition: `width 0.5s ease-out ${i * 0.08}s` }} />
              ))}
            </div>

            {/* 2×2 boxes spanning the right two-thirds */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: sc(8), columnGap: 16 }}>
              <StoryBox color={BOXES[0][0]} count={BOXES[0][1]} label={BOXES[0][2]} />
              <StoryBox color={BOXES[1][0]} count={BOXES[1][1]} label={BOXES[1][2]} />
              <StoryBox color={BOXES[2][0]} count={BOXES[2][1]} label={BOXES[2][2]} />
              <StoryBox color={BOXES[3][0]} count={BOXES[3][1]} label={BOXES[3][2]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
